import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiFileText,
    FiRefreshCw,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchHsnSummary,

    outwardHsnSummarySelector,
    inwardHsnSummarySelector,
    hsnSummaryTotalsSelector,
    hsnSummaryCountsSelector,
    hsnSummaryMetaSelector,
    hsnSummaryWarningsSelector,

    gstReportLoadingSelector,
    gstReportErrorSelector,
} from "../../../../../../../store/billing/gst/gstReportSlice";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        toNumber(value)
    );


const formatQuantity = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            maximumFractionDigits: 3,
        }
    ).format(
        toNumber(value)
    );


const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(date);
};


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const HsnSummaryReport = ({
    fromDate,
    toDate,
    financialYear,
}) => {
    const dispatch =
        useDispatch();


    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const outward =
        useSelector(
            outwardHsnSummarySelector
        ) || [];

    const inward =
        useSelector(
            inwardHsnSummarySelector
        ) || [];

    const totals =
        useSelector(
            hsnSummaryTotalsSelector
        ) || {};

    const counts =
        useSelector(
            hsnSummaryCountsSelector
        ) || {};

    const meta =
        useSelector(
            hsnSummaryMetaSelector
        ) || {};

    const warnings =
        useSelector(
            hsnSummaryWarningsSelector
        ) || [];

    const loading =
        useSelector(
            gstReportLoadingSelector
        ) || {};

    const errors =
        useSelector(
            gstReportErrorSelector
        ) || {};


    /*
    |--------------------------------------------------------------------------
    | ACTIVE SECTION
    |--------------------------------------------------------------------------
    */

    const [
        activeSection,
        setActiveSection,
    ] = useState(
        "OUTWARD"
    );


    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */

    const loadData =
        useCallback(
            async () => {
                await dispatch(
                    fetchHsnSummary({
                        fromDate,
                        toDate,
                        financialYear,
                    })
                );
            },
            [
                dispatch,
                fromDate,
                toDate,
                financialYear,
            ]
        );


    useEffect(
        () => {
            loadData();
        },
        [
            loadData,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | ACTIVE DATA
    |--------------------------------------------------------------------------
    */

    const activeRecords =
        activeSection ===
            "OUTWARD"
            ? outward
            : inward;


    const activeTotals =
        activeSection ===
            "OUTWARD"
            ? totals?.outward || {}
            : totals?.inward || {};


    /*
    |--------------------------------------------------------------------------
    | SUMMARY CARDS
    |--------------------------------------------------------------------------
    */

    const cards =
        useMemo(
            () => [
                {
                    label:
                        "Outward HSN",

                    value:
                        toNumber(
                            counts?.outwardHsn
                        ),

                    help:
                        `${toNumber(
                            counts?.salesInvoices
                        )} sales invoices`,
                },

                {
                    label:
                        "Inward HSN",

                    value:
                        toNumber(
                            counts?.inwardHsn
                        ),

                    help:
                        `${toNumber(
                            counts?.purchaseBills
                        )} purchase bills`,
                },

                {
                    label:
                        "Outward Taxable",

                    value:
                        formatCurrency(
                            totals
                                ?.outward
                                ?.taxableValue
                        ),

                    help:
                        "Net of sales credit notes",
                },

                {
                    label:
                        "Inward Taxable",

                    value:
                        formatCurrency(
                            totals
                                ?.inward
                                ?.taxableValue
                        ),

                    help:
                        "Net of purchase debit notes",
                },
            ],
            [
                counts,
                totals,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="hsn-summary-report">

            {/* =====================================================
                ERROR
            ===================================================== */}

            {errors.hsnSummary && (

                <div className="gst-dashboard-alert">

                    <FiAlertCircle />

                    <span>
                        {errors.hsnSummary}
                    </span>

                </div>

            )}


            {/* =====================================================
                WARNINGS
            ===================================================== */}

            {warnings.map(
                (
                    warning,
                    index
                ) => (

                    <div
                        key={index}
                        className="hsn-summary-warning"
                    >

                        <FiAlertCircle />

                        <span>
                            {warning}
                        </span>

                    </div>

                )
            )}


            {/* =====================================================
                MAIN SUMMARY
            ===================================================== */}

            <section className="gst-dashboard-summary-grid">

                {cards.map(
                    (
                        card
                    ) => (

                        <div
                            key={
                                card.label
                            }
                            className="gst-dashboard-summary-card"
                        >

                            <span>
                                {card.label}
                            </span>

                            <strong>
                                {card.value}
                            </strong>

                            <small>
                                {card.help}
                            </small>

                        </div>

                    )
                )}

            </section>


            {/* =====================================================
                DOCUMENT COUNTS
            ===================================================== */}

            <section className="hsn-summary-count-grid">

                <div>

                    <span>
                        Sales Invoices
                    </span>

                    <strong>
                        {toNumber(
                            counts?.salesInvoices
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Purchase Bills
                    </span>

                    <strong>
                        {toNumber(
                            counts?.purchaseBills
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Credit Notes
                    </span>

                    <strong>
                        {toNumber(
                            counts?.creditNotes
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Debit Notes
                    </span>

                    <strong>
                        {toNumber(
                            counts?.debitNotes
                        )}
                    </strong>

                </div>

            </section>


            {/* =====================================================
                SECTION TABS
            ===================================================== */}

            <section className="hsn-summary-tabs">

                <button
                    type="button"
                    className={
                        activeSection ===
                            "OUTWARD"
                            ? "active"
                            : ""
                    }
                    onClick={() =>
                        setActiveSection(
                            "OUTWARD"
                        )
                    }
                >
                    Outward / Sales

                    <span>
                        {outward.length}
                    </span>
                </button>


                <button
                    type="button"
                    className={
                        activeSection ===
                            "INWARD"
                            ? "active"
                            : ""
                    }
                    onClick={() =>
                        setActiveSection(
                            "INWARD"
                        )
                    }
                >
                    Inward / Purchase

                    <span>
                        {inward.length}
                    </span>
                </button>


                <div className="hsn-summary-refresh">

                    <button
                        type="button"
                        className="gst-dashboard-button"
                        disabled={
                            loading.hsnSummary
                        }
                        onClick={
                            loadData
                        }
                    >

                        <FiRefreshCw
                            className={
                                loading.hsnSummary
                                    ? "gst-dashboard-spinner"
                                    : ""
                            }
                        />

                        Refresh

                    </button>

                </div>

            </section>


            {/* =====================================================
                ACTIVE TOTALS
            ===================================================== */}

            <section className="hsn-summary-active-totals">

                <div>

                    <span>
                        Quantity
                    </span>

                    <strong>
                        {formatQuantity(
                            activeTotals
                                ?.quantity
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Total Value
                    </span>

                    <strong>
                        {formatCurrency(
                            activeTotals
                                ?.totalValue
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Taxable
                    </span>

                    <strong>
                        {formatCurrency(
                            activeTotals
                                ?.taxableValue
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Total Tax
                    </span>

                    <strong>
                        {formatCurrency(
                            activeTotals
                                ?.totalTax
                        )}
                    </strong>

                </div>

            </section>


            {/* =====================================================
                TABLE
            ===================================================== */}

            <section className="hsn-summary-table-card">

                {loading.hsnSummary &&
                activeRecords.length ===
                    0 ? (

                    <div className="hsn-summary-empty">

                        <FiRefreshCw className="gst-dashboard-spinner" />

                        <strong>
                            Loading HSN summary...
                        </strong>

                    </div>

                ) : activeRecords.length ===
                    0 ? (

                    <div className="hsn-summary-empty">

                        <FiFileText />

                        <strong>
                            No HSN records found
                        </strong>

                        <span>
                            Check the selected GST period and item HSN codes.
                        </span>

                    </div>

                ) : (

                    <div className="hsn-summary-table-wrapper">

                        <table className="hsn-summary-table">

                            <thead>

                                <tr>

                                    <th>
                                        HSN / SAC
                                    </th>

                                    <th>
                                        Description
                                    </th>

                                    <th>
                                        UQC
                                    </th>

                                    <th className="text-right">
                                        GST Rate
                                    </th>

                                    <th className="text-right">
                                        Quantity
                                    </th>

                                    <th className="text-right">
                                        Total Value
                                    </th>

                                    <th className="text-right">
                                        Taxable
                                    </th>

                                    <th className="text-right">
                                        IGST
                                    </th>

                                    <th className="text-right">
                                        CGST
                                    </th>

                                    <th className="text-right">
                                        SGST
                                    </th>

                                    <th className="text-right">
                                        Cess
                                    </th>

                                    <th className="text-right">
                                        Total Tax
                                    </th>

                                    <th className="text-right">
                                        Documents
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {activeRecords.map(
                                    (
                                        item,
                                        index
                                    ) => (

                                        <tr
                                            key={
                                                `${item?.hsnCode}-${item?.gstRate}-${item?.uqc}-${index}`
                                            }
                                        >

                                            <td>

                                                <strong
                                                    className={
                                                        item
                                                            ?.hsnCode ===
                                                        "NO HSN"
                                                            ? "hsn-summary-code hsn-summary-code-missing"
                                                            : "hsn-summary-code"
                                                    }
                                                >
                                                    {item?.hsnCode ||
                                                        "-"}
                                                </strong>

                                            </td>


                                            <td>
                                                {item?.description ||
                                                    "-"}
                                            </td>


                                            <td>

                                                <span className="hsn-summary-uqc">

                                                    {item?.uqc ||
                                                        "-"}

                                                </span>

                                            </td>


                                            <td className="text-right">

                                                <strong>
                                                    {toNumber(
                                                        item
                                                            ?.gstRate
                                                    )}
                                                    %
                                                </strong>

                                            </td>


                                            <td className="text-right">

                                                {formatQuantity(
                                                    item
                                                        ?.quantity
                                                )}

                                            </td>


                                            <td className="text-right">

                                                {formatCurrency(
                                                    item
                                                        ?.totalValue
                                                )}

                                            </td>


                                            <td className="text-right">

                                                {formatCurrency(
                                                    item
                                                        ?.taxableValue
                                                )}

                                            </td>


                                            <td className="text-right">

                                                {formatCurrency(
                                                    item
                                                        ?.igstAmount
                                                )}

                                            </td>


                                            <td className="text-right">

                                                {formatCurrency(
                                                    item
                                                        ?.cgstAmount
                                                )}

                                            </td>


                                            <td className="text-right">

                                                {formatCurrency(
                                                    item
                                                        ?.sgstAmount
                                                )}

                                            </td>


                                            <td className="text-right">

                                                {formatCurrency(
                                                    item
                                                        ?.cessAmount
                                                )}

                                            </td>


                                            <td className="text-right">

                                                <strong className="hsn-summary-tax">

                                                    {formatCurrency(
                                                        item
                                                            ?.totalTax
                                                    )}

                                                </strong>

                                            </td>


                                            <td className="text-right">

                                                {toNumber(
                                                    item
                                                        ?.documentCount
                                                )}

                                            </td>

                                        </tr>

                                    )
                                )}

                            </tbody>

                        </table>

                    </div>

                )}

            </section>


            {/* =====================================================
                META
            ===================================================== */}

            <section className="hsn-summary-meta">

                <div>

                    <span>
                        From
                    </span>

                    <strong>
                        {formatDate(
                            meta?.fromDate
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        To
                    </span>

                    <strong>
                        {formatDate(
                            meta?.toDate
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Financial Year
                    </span>

                    <strong>
                        {meta?.financialYear ||
                            "-"}
                    </strong>

                </div>


                <div>

                    <span>
                        Generated
                    </span>

                    <strong>
                        {formatDate(
                            meta?.generatedAt
                        )}
                    </strong>

                </div>

            </section>

        </div>
    );
};


export default HsnSummaryReport;